import { CLIPBOARD_FORMAT_TEXT } from '../constants/clipboard'
import { EMPTY_STRING } from '../constants/common'
import { EVENT_NAME_COPY } from '../constants/event'
import {
  HTML_DATA_PREFIX,
  HTML_DATA_SEPARATOR,
  HTML_LINK_PROTOCOL_MAILTO,
  HTML_LINK_PROTOCOL_TEL,
  HTML_LINK_TARGET,
  HTML_MAX_TEXT_NODES,
  HTML_MIME_TYPE,
  HTML_OPERATOR_EQUALS,
} from '../constants/html'
import {
  REG_EXP_EMAIL_IN_TEXT,
  REG_EXP_HTML_ANCHOR_PRESENT,
  REG_EXP_NEW_LINE,
  REG_EXP_URL_IN_TEXT,
  REG_EXP_URL_PRESENT,
  REG_EXP_URL_START,
  REG_EXP_URL_STARTS_WITH_WWW,
} from '../constants/regExp'

import { preventDefault } from './events'

// A module-level cache, not a constant: one DOMParser is created on first use
// and shared by every call that parses markup.
let parser = null

// www.* → https://www.* so the anchor href is absolute, not site-relative.
function normalizeUrlHref(rawUrl) {
  return REG_EXP_URL_STARTS_WITH_WWW.test(rawUrl) ? `https://${rawUrl}` : rawUrl
}

function appendTextWithEmailLinks(doc, fragment, text) {
  if (!REG_EXP_EMAIL_IN_TEXT.test(text)) {
    fragment.appendChild(doc.createTextNode(text))

    return
  }

  text
    .split(REG_EXP_EMAIL_IN_TEXT)
    .filter(part => part != null && part !== EMPTY_STRING)
    .forEach(part => {
      if (REG_EXP_EMAIL_IN_TEXT.test(part)) {
        const anchor = doc.createElement('a')

        anchor.setAttribute('href', `mailto:${part}`)
        anchor.textContent = part

        fragment.appendChild(anchor)
      } else {
        fragment.appendChild(doc.createTextNode(part))
      }
    })
}

// Split a text node's value into text/URL parts and wrap the URLs in anchors.
// Plain-text parts are additionally scanned for bare email addresses.
function buildLinkifiedFragment(doc, text) {
  const fragment = doc.createDocumentFragment()

  const parts = text
    .split(REG_EXP_URL_IN_TEXT)
    .filter(part => part != null && part !== EMPTY_STRING)

  parts.forEach(part => {
    if (REG_EXP_URL_START.test(part)) {
      const anchor = doc.createElement('a')

      anchor.setAttribute('href', normalizeUrlHref(part))
      anchor.setAttribute('target', HTML_LINK_TARGET.blank)
      anchor.setAttribute('rel', 'noopener noreferrer nofollow')
      anchor.textContent = part

      fragment.appendChild(anchor)
    } else {
      appendTextWithEmailLinks(doc, fragment, part)
    }
  })

  return fragment
}

export function isHtmlLinkTargetBlank(target) {
  return target === HTML_LINK_TARGET.blank
}

/**
 * @returns {DOMParser}
 */
export function getDOMParser() {
  return parser ?? (parser = new DOMParser())
}

/**
 * @param {string} text
 *
 * @returns {Document}
 */
export function toDocument(text) {
  return getDOMParser().parseFromString(text, HTML_MIME_TYPE)
}

/**
 * @param {Document} doc
 *
 * @returns {Document}
 */
export function normalizeDocument(doc) {
  doc.querySelectorAll('p').forEach(item => {
    if (item.getAttribute('style') || item.classList.contains('MsoNormal')) {
      item.style.margin = '0px'
    }
  })

  doc.normalize()

  return doc
}

/**
 * @param {string} html
 *
 * @returns {string}
 */
export function normalizeHtml(html) {
  const doc = normalizeDocument(toDocument(html))

  // Only replace paragraphs whose sole child is a <br> — the TinyMCE pattern
  // for empty lines (<p><br data-mce-bogus="1"></p> or <p><br></p>).
  // Real content <br> tags inside non-empty paragraphs are left untouched.
  doc.querySelectorAll('p').forEach(p => {
    if (p.childNodes.length === 1 && p.firstChild.nodeName === 'BR') {
      p.innerHTML = '&nbsp;'
    }
  })

  return doc.body.innerHTML.trim().replace(/\n+/g, EMPTY_STRING)
}

/**
 * Auto-links bare URLs inside an HTML string while preserving the existing
 * markup. Text already inside an `<a>` is left untouched to avoid nested links.
 *
 * @param {string} html
 *
 * @returns {string}
 */
export function linkifyHtml(html) {
  const source = html || EMPTY_STRING

  if (
    !REG_EXP_URL_PRESENT.test(source) &&
    !REG_EXP_EMAIL_IN_TEXT.test(source)
  ) {
    return source
  }

  const doc = toDocument(source)

  doc.body.normalize()

  const walker = doc.createTreeWalker(doc.body, NodeFilter?.SHOW_TEXT || 4)
  const textNodes = []

  // Cap how many text nodes we walk (not just how many contain URLs), so the
  // fail-safe also bounds large payloads with few/no extra URL nodes.
  let visited = 0

  while (visited < HTML_MAX_TEXT_NODES && walker.nextNode()) {
    visited += 1

    const node = walker.currentNode

    if (
      node.nodeValue &&
      (REG_EXP_URL_PRESENT.test(node.nodeValue) ||
        REG_EXP_EMAIL_IN_TEXT.test(node.nodeValue)) &&
      !node.parentElement?.closest('a, style, script')
    ) {
      textNodes.push(node)
    }
  }

  textNodes.forEach(textNode => {
    if (textNode.parentNode) {
      textNode.parentNode.replaceChild(
        buildLinkifiedFragment(doc, textNode.nodeValue || EMPTY_STRING),
        textNode
      )
    }
  })

  return doc.body.innerHTML
}

/**
 * Forces the anchors of an HTML string to open in a new tab. In-page hash
 * links and mailto:/tel: anchors are left untouched.
 *
 * @param {string} html
 *
 * @returns {string}
 */
export function openHtmlLinksInNewTab(html) {
  const source = html || EMPTY_STRING

  if (!REG_EXP_HTML_ANCHOR_PRESENT.test(source)) {
    return source
  }

  const doc = toDocument(source)

  doc.body.querySelectorAll('a[href]').forEach(anchor => {
    // Browsers resolve hrefs ignoring leading whitespace and scheme case.
    const href = anchor.getAttribute('href').trim().toLowerCase()

    if (
      !href.startsWith('#') &&
      !href.startsWith(HTML_LINK_PROTOCOL_MAILTO) &&
      !href.startsWith(HTML_LINK_PROTOCOL_TEL)
    ) {
      anchor.setAttribute('target', HTML_LINK_TARGET.blank)
      anchor.relList.remove('opener')
      anchor.relList.add('noopener', 'noreferrer')
    }
  })

  return doc.body.innerHTML
}

/**
 * @param {string} html
 *
 * @returns {string}
 */
export function newLineToBrInHtml(html) {
  const source = html || EMPTY_STRING

  if (!REG_EXP_NEW_LINE.test(source)) {
    return source
  }

  const doc = toDocument(source)

  const walker = doc.createTreeWalker(doc.body, NodeFilter?.SHOW_TEXT || 4)
  const textNodes = []

  let visited = 0

  while (visited < HTML_MAX_TEXT_NODES && walker.nextNode()) {
    visited += 1

    const node = walker.currentNode

    if (
      node.nodeValue &&
      REG_EXP_NEW_LINE.test(node.nodeValue) &&
      node.nodeValue.trim() !== EMPTY_STRING &&
      !node.parentElement?.closest('pre, style, script')
    ) {
      textNodes.push(node)
    }
  }

  if (textNodes.length === 0) {
    return source
  }

  textNodes.forEach(textNode => {
    const fragment = doc.createDocumentFragment()

    textNode.nodeValue.split(REG_EXP_NEW_LINE).forEach((part, index) => {
      if (index > 0) {
        fragment.appendChild(doc.createElement('br'))
      }

      if (part !== EMPTY_STRING) {
        fragment.appendChild(doc.createTextNode(part))
      }
    })

    textNode.parentNode?.replaceChild(fragment, textNode)
  })

  return doc.body.innerHTML
}

/**
 * @param {string} text
 *
 * @returns {HTMLElement}
 */
export function toHTMLElement(text) {
  return toDocument(text).body
}

/**
 * @param {string} text
 *
 * @returns {string}
 */
export function toHTML(text) {
  return text === EMPTY_STRING ? EMPTY_STRING : toHTMLElement(text).innerHTML
}

/**
 * @param {string} text
 *
 * @returns {string}
 */
export function toText(text) {
  let nextText = EMPTY_STRING

  if (text !== EMPTY_STRING) {
    const element = toHTMLElement(text)

    element.normalize()
    nextText = element.innerText
  }

  return nextText
}

/**
 * @param {string} name
 * @param {string | null} value
 * @param {string} operator
 *
 * @returns {string}
 */
export function getAttributeSelector(
  name,
  value = null,
  operator = HTML_OPERATOR_EQUALS
) {
  const valueSelector =
    value == null ? EMPTY_STRING : `${operator}${JSON.stringify(String(value))}`

  return `[${CSS.escape(name)}${valueSelector}]`
}

/**
 * @param {string} name
 *
 * @returns {string}
 */
export function getDataAttributeName(name) {
  return (
    HTML_DATA_PREFIX +
    name.replace(/[A-Z]/g, $0 => HTML_DATA_SEPARATOR + $0.toLowerCase())
  )
}

/**
 * @param {string} name
 * @param {string | null} value
 *
 * @returns {string}
 */
export function getDataAttributeSelector(name, value = null) {
  return getAttributeSelector(getDataAttributeName(name), value)
}

/**
 * @param {HTMLElement} element
 * @param {string} name
 *
 * @returns {?string}
 */
export function getElementDataValue(element, name) {
  return element.dataset[name]
}

/**
 * @param {HTMLElement} element
 * @param {string} name
 * @param {string | null} value
 */
export function setElementDataValue(element, name, value) {
  if (value == null) {
    delete element.dataset[name]
  } else {
    element.dataset[name] = value
  }
}

/**
 * @param {HTMLElement} element
 * @param {object} data
 */
export function setElementData(element, data) {
  Object.entries(data).forEach(([key, value]) => {
    setElementDataValue(element, key, value)
  })
}

/**
 * Converts plain text (with newline characters) to TinyMCE-compatible HTML.
 * Every newline becomes a <br> inside a single paragraph wrapper.
 *
 * @param {string} text
 *
 * @returns {string}
 */
export function plainTextToHtml(text) {
  if (!text) {
    return EMPTY_STRING
  }

  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

  return `<p>${escaped.replace(/\n/g, '<br>')}</p>`
}

/**
 * Strips HTML markup but preserves paragraph and line breaks. Block elements
 * are separated with a blank line, `<br>` becomes a single newline. Suitable
 * for inserting rich content (e.g. AI-generated HTML) into a plain-text input.
 *
 * NOTE: only the body's direct children are treated as blocks. Nested block
 * elements (e.g. `<div><p>A</p><p>B</p></div>`) collapse into a single block,
 * separated only by stray `<br>` newlines. The expected input shape is a flat
 * sequence of paragraphs, which is what the AI text generator produces.
 *
 * @param {string} htmlString
 *
 * @returns {string}
 */
export function htmlToMultilineText(htmlString) {
  if (!htmlString) {
    return EMPTY_STRING
  }

  const doc = toDocument(htmlString)

  doc.querySelectorAll('br').forEach(br => br.replaceWith('\n'))

  const blocks = Array.from(doc.body.children)
    .map(el => el.textContent.trim())
    .filter(Boolean)

  const raw =
    blocks.length > 0
      ? blocks.join('\n\n')
      : doc.body.textContent || EMPTY_STRING

  return raw
    .replace(/[ \t]+/g, ' ')
    .replace(/[ \t]*\n[ \t]*/g, '\n')
    .trim()
}

/**
 * Returns true only when the TinyMCE-produced HTML contains visible text after
 * stripping bookmark spans (`[data-mce-type]`). Bookmarks are invisible cursor
 * markers that TinyMCE injects into the DOM and can serialize into a Formik
 * field value, causing false positives when checking for user content.
 *
 * @param {string} html
 *
 * @returns {boolean}
 */
export function hasTinyMceHtmlVisibleText(html) {
  if (!html) {
    return false
  }

  const el = toHTMLElement(html)

  el.querySelectorAll('[data-mce-type]').forEach(node => node.remove())

  return el.textContent.trim().length > 0
}

/**
 * Returns true when the TinyMCE-produced HTML contains any visible content —
 * text, an inline image, or a table — after stripping bookmark spans
 * (`[data-mce-type]`). Use this when an inline image or table alone should
 * count as content (e.g. for enabling a send button).
 *
 * @param {string} html
 *
 * @returns {boolean}
 */
export function hasTinyMceHtmlVisibleContent(html) {
  return (
    Boolean(html) &&
    (hasTinyMceHtmlVisibleText(html) ||
      toHTMLElement(html).querySelector('img, table') !== null)
  )
}

export function htmlToPlainText(htmlString) {
  if (!htmlString) {
    return EMPTY_STRING
  }

  const el = document.createElement('div')

  el.innerHTML = htmlString

  let text = el.innerText || el.textContent || EMPTY_STRING

  text = text
    .replace(/\s{2,}/g, ' ')
    .replace(/\n\s*\n+/g, '\n')
    .trim()

  return text
}

export function isHTMLElementFocused(ref) {
  return ref.current === document.activeElement
}

export function copyHtmlWithDom(html, text) {
  const el = document.createElement('div')
  const selection = window.getSelection()

  // Set as is: the browser would copy the page's computed (dark) styles
  function handleCopy(event) {
    event.clipboardData.setData(HTML_MIME_TYPE, html)
    event.clipboardData.setData(CLIPBOARD_FORMAT_TEXT, text)
    preventDefault(event)
  }

  try {
    el.setAttribute('contenteditable', 'true')
    el.style.cssText = 'position:fixed;left:-9999px;top:0'
    el.innerHTML = html
    document.body.appendChild(el)

    const range = document.createRange()

    range.selectNodeContents(el)
    selection.removeAllRanges()
    selection.addRange(range)

    document.addEventListener(EVENT_NAME_COPY, handleCopy)

    return document.execCommand('copy')
  } catch {
    return false
  } finally {
    document.removeEventListener(EVENT_NAME_COPY, handleCopy)
    selection?.removeAllRanges()
    el.remove()
  }
}
